import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, ListItemText, TextField, Typography } from '@mui/material'
import { memo, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectUserSecurityExpressions } from '../../selectors/security'
import { SecurityExpression } from '../../proto/generated/security'
import { makeStyles } from 'tss-react/mui'

export type ExpressionNameDialogProps = {
    onClosed: Function | undefined
    showGivenNames?: boolean
    expression?: string
}

const useStyles = makeStyles()((theme) => ({
    textFieldInput: {
        '& .MuiInputBase-input': {
            padding: theme.spacing(1)
        }
    },
    givenNamesContainer: {
        paddingTop: theme.spacing(2)
    },
    givenNamesList: {
        maxHeight: 300,
        overflow: 'auto'
    },
    dialogDescription: {
        padding: theme.spacing(1)
    }
}))

const ExpressionNameDialog = (props: ExpressionNameDialogProps) => {
    const { onClosed, showGivenNames = false, expression = undefined } = props
    const [open, setOpen] = useState<boolean>(true)
    const savedExpressions = useSelector(selectUserSecurityExpressions)
    const existingExpression = useMemo(() => (savedExpressions ?? []).find((expn: SecurityExpression) => expn.expression === expression) ?? undefined, [savedExpressions, expression])
    const [name, setName] = useState<string>(existingExpression?.name ?? '')
    const givenNames = useMemo(() => new Set<string>((savedExpressions ?? []).map((expn: SecurityExpression) => expn.name)), [savedExpressions])
    const { classes } = useStyles()
    const onDialogClosed = useCallback(() => {
        setOpen(false)
        if (onClosed) {
            onClosed(null)
        }
    }, [])

    const onDialogSubmitted = useCallback(() => {
        if (onClosed) {
            if (existingExpression) {
                onClosed(name.trim(), existingExpression.id)
            } else {
                onClosed(name.trim())
            }
        } else {
            setOpen(false)
        }
    }, [onClosed, name])

    const error = useMemo(() => {
        return givenNames.has(name)
    }, [name, givenNames])

    const onKeyUp = useCallback(
        (e: any) => {
            if (e.key === 'Enter') {
                if (!error && name.trim().length > 0) {
                    onDialogSubmitted()
                }
            }
            e.stopPropagation()
        },
        [onDialogSubmitted, error, name]
    )

    const onChange = useCallback((e: any) => {
        setName(e.target.value)
    }, [])

    return (
        <Dialog open={open} onClose={onDialogClosed} maxWidth={'sm'} fullWidth aria-labelledby="expression-name-dialog-title" aria-describedby="expression-name-dialog-description">
            <DialogContent>
                <DialogContentText id="expression-name-dialog-description">Give a name for your expression</DialogContentText>
                <TextField
                    variant="outlined"
                    fullWidth
                    multiline={false}
                    value={name}
                    onKeyUp={onKeyUp}
                    onChange={onChange}
                    error={error}
                    className={classes.textFieldInput}
                    helperText={error ? 'Name already taken' : ''}
                    autoFocus
                />
                {showGivenNames && givenNames.size > 0 && (
                    <Box className={classes.givenNamesContainer}>
                        <Typography variant="subtitle1" className={classes.dialogDescription}>
                            {' '}
                            Names already taken :{' '}
                        </Typography>
                        <Box className={classes.givenNamesList}>
                            {Array.from(givenNames).map((nm, index) => (
                                <ListItemText secondary={nm} />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogSubmitted} autoFocus={!error} variant={'contained'} disabled={error || name.trim().length < 1}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default memo(ExpressionNameDialog)
